import { MainSection } from 'src/components/design-system/struct/MainSection';
import { StudentLayout } from 'src/components/Layout/student/StudentLayout';
import { AuthorizedUserProps } from 'src/routes/components/PrivateRoute';
import React, { FC, ReactElement, useState } from 'react';
import { getUserFullname } from 'src/utils/getFullname';
import { Avatar } from 'src/components/Avatar';
import { ConfigProvider, Dropdown, Empty, Popover, Table } from 'antd';
import { EllipsisOutlined, MoreOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { filter, sortBy, sumBy } from 'lodash';
import { PrimaryButton } from 'src/components/design-system/form/buttons/PrimaryButton';
import { Form } from 'src/components/design-system/form';
import { FormButtons } from 'src/components/design-system/form/buttons';
import { FormikErrors, useFormik } from 'formik';
import {
    DateField,
    FormRow,
    IntegerField,
    TextField,
} from 'src/forms/DebitStudentCoins';
import { ApplicationModal } from 'src/components/Modal/LegacyModal';
import './module.scss';
import { useParams } from 'react-router-dom';
import {
    useCancelBranchStudentDebtMutation,
    useGetBranchStudentDebtsQuery,
    useRegisterBranchStudentDebtMutation,
    useRegisterBranchStudentDebtPaymentMutation,
    useUpdateBranchStudentDebtMutation,
} from 'src/api/endpoints/students-debts';
import { Student, useGetStudentByIdQuery } from 'src/api/endpoints/students';
import { Loader } from 'src/components/shared/loader';
import { notifyAboutException, notifyAboutSuccess } from 'src/utils/notify';
import { ResponseError } from 'src/api';
import { Helmet } from 'react-helmet-async';

export enum DebtStatus {
    Active = 0,
    Cancelled = 1,
    Paid = 2,
}

const DebtStatusOptions: Record<
    DebtStatus,
    { label: string; color: string; background: string }
> = {
    [DebtStatus.Active]: {
        label: 'Активна',
        color: 'white',
        background: '#f13036',
    },
    [DebtStatus.Cancelled]: {
        label: 'Аннулирована',
        color: '#868686',
        background: '#e8e8e8',
    },
    [DebtStatus.Paid]: {
        label: 'Погашена',
        color: '#26c50c',
        background: '#e1fce3',
    },
};

type DebtBase = {
    id: number;
    amount: string;
    description: string;
    originatedAt: number;
};

type ClosedDebt = DebtBase & {
    closedAt: number;
    closedBy: number;
};

type OpenedDebt = DebtBase & {
    closedAt?: null;
    closedBy?: null;
    paidAt?: null;
    cancelledAt?: null;
};

type PaidDebt = ClosedDebt & {
    status: DebtStatus.Paid;
    paidAt: number;
    cancelledAt?: null;
};

type CancelledDebt = ClosedDebt & {
    status: DebtStatus.Cancelled;
    cancelledAt: number;
    paidAt?: null;
};

export type ActiveDebt = OpenedDebt & {
    status: DebtStatus.Active;
};

export type StudentDebt = ActiveDebt | CancelledDebt | PaidDebt;

export const BranchStudentDebtsPage: FC<AuthorizedUserProps> = (
    props: AuthorizedUserProps
): ReactElement => {
    const { studentId: stringifyStudentId } = useParams<{
        studentId: string;
    }>();
    const studentId = Number(stringifyStudentId);

    const { data: student, isLoading: isStudentLoading } =
        useGetStudentByIdQuery({
            params: {
                branchId: props.currentBranchId,
                studentId: studentId,
            },
        });

    const { data: studentDebts, isLoading: isStudentDebtsLoading } =
        useGetBranchStudentDebtsQuery({
            params: {
                branchId: props.currentBranchId,
                studentId: studentId,
            },
        });

    const total: number = sumBy(
        filter(studentDebts, d => d.status === DebtStatus.Active),
        d => Number(d.amount)
    );

    return (
        <StudentLayout auth={props}>
            {!!student && (
                <Helmet
                    title={`Задолженности · ${getUserFullname(student)} · ${props.currentBranch?.name} · Личный кабинет`}
                />
            )}
            <MainSection
                title={'Задолженности студента'}
                actions={
                    <RegisterDebtWidget
                        branchId={props.currentBranchId}
                        student={student}
                    />
                }
            >
                <div className={'student-block'}>
                    {isStudentLoading || !student ? (
                        <Loader loader={{ color: '#dadee0', strokeWidth: 3 }} />
                    ) : (
                        <>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'start',
                                    alignItems: 'center',
                                    padding: '15px',
                                    gap: '15px',
                                }}
                            >
                                <Avatar
                                    userId={student.userId}
                                    size={80}
                                    radius={15}
                                />
                                <div
                                    className="student-information"
                                    style={{
                                        height: '100%',
                                        display: 'flex',
                                        justifyContent: 'space-around',
                                        flexDirection: 'column',
                                        alignItems: 'start',
                                        gap: '5px',
                                    }}
                                >
                                    <div
                                        className="fullname"
                                        style={{
                                            fontSize: '18px',
                                            fontWeight: '500',
                                            color: '#092433',
                                        }}
                                    >
                                        {getUserFullname({
                                            name: student.firstname,
                                            ...student,
                                        })}
                                    </div>
                                    <div
                                        className="contract"
                                        style={{
                                            fontSize: '14px',
                                            color: '#092433',
                                        }}
                                    >
                                        {student.contract}
                                    </div>
                                </div>
                            </div>

                            {total > 0 && (
                                <div
                                    className="total-debt"
                                    style={{
                                        height: '100%',
                                        display: 'flex',
                                        justifyContent: 'space-around',
                                        alignItems: 'end',
                                        flexDirection: 'column',
                                        padding: '15px 15px 15px 30px',
                                        borderRadius: '5px',
                                        background: '#f13036',
                                        border: '1px solid #f13036',
                                    }}
                                >
                                    <div
                                        className="title"
                                        style={{
                                            color: '#ffefef',
                                            fontSize: '16px',
                                        }}
                                    >
                                        Общая задолженность
                                    </div>
                                    <div
                                        className="value"
                                        style={{
                                            color: 'white',
                                            fontSize: '30px',
                                            fontWeight: '500',
                                        }}
                                    >
                                        {total.toLocaleString('ru-RU') + ' ₽'}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div
                    className={'table'}
                    style={{ width: '100%', marginTop: '24px' }}
                >
                    <ConfigProvider
                        renderEmpty={() => {
                            return (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={'Задолженности отсутствуют'}
                                />
                            );
                        }}
                    >
                        <Table<StudentDebt>
                            tableLayout={'fixed'}
                            dataSource={sortBy(studentDebts, 'id').reverse()}
                            loading={isStudentDebtsLoading}
                            rowKey={d => d.id}
                            pagination={false}
                        >
                            <Table.Column<StudentDebt>
                                width={'auto'}
                                title={'Дата регистрации'}
                                render={(_, debt: StudentDebt) => {
                                    return dayjs(debt.originatedAt).format(
                                        'DD.MM.YYYY'
                                    );
                                }}
                            />

                            <Table.Column<StudentDebt>
                                width={'30%'}
                                title={'Описание'}
                                render={(_, { description }: StudentDebt) => {
                                    if (description.length <= 40) {
                                        return (
                                            <div
                                                className={'description'}
                                                style={{
                                                    fontSize: '14px',
                                                }}
                                            >
                                                {description}
                                            </div>
                                        );
                                    }

                                    return (
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                gap: '10px',
                                            }}
                                        >
                                            <div className="description">
                                                {description.slice(0, 40) +
                                                    '...'}
                                            </div>
                                            <Popover
                                                arrow={false}
                                                content={
                                                    <div
                                                        className={
                                                            'description-popover-content'
                                                        }
                                                        style={{
                                                            fontSize: '14px',
                                                        }}
                                                    >
                                                        {description}
                                                    </div>
                                                }
                                                placement={'bottom'}
                                                trigger={['hover']}
                                            >
                                                <div
                                                    style={{
                                                        padding: '5px',
                                                        background: '#eaeeee',
                                                        display: 'flex',
                                                        justifyContent:
                                                            'center',
                                                        alignItems: 'center',
                                                        borderRadius: '5px',
                                                        cursor: 'help',
                                                    }}
                                                >
                                                    <EllipsisOutlined />
                                                </div>
                                            </Popover>
                                        </div>
                                    );
                                }}
                            />

                            <Table.Column<StudentDebt>
                                width={'10%'}
                                title={'Сумма'}
                                render={(_, debt: StudentDebt) => {
                                    return (
                                        Number(debt.amount).toLocaleString(
                                            'ru-RU'
                                        ) + ' ₽'
                                    );
                                }}
                            />

                            <Table.Column<StudentDebt>
                                width={'15%'}
                                title={'Статус'}
                                render={(_, debt: StudentDebt) => {
                                    return (
                                        <div
                                            className={'debt-status'}
                                            style={{
                                                width: 'max-content',
                                                minWidth: '110px',
                                                color: DebtStatusOptions[
                                                    debt.status
                                                ].color,
                                                fontSize: '12px',
                                                padding: '6px 12px',
                                                borderRadius: '6px',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                background:
                                                    DebtStatusOptions[
                                                        debt.status
                                                    ].background,
                                            }}
                                        >
                                            {
                                                DebtStatusOptions[debt.status]
                                                    .label
                                            }
                                        </div>
                                    );
                                }}
                            />

                            <Table.Column<StudentDebt>
                                width={'auto'}
                                title={'Дата закрытия'}
                                render={(_, debt: StudentDebt) => {
                                    if (debt.status === DebtStatus.Active)
                                        return;

                                    let timestamp: number = debt.closedAt;

                                    if (debt.status === DebtStatus.Paid) {
                                        timestamp = debt.paidAt;
                                    }

                                    return dayjs(timestamp).format(
                                        'DD.MM.YYYY'
                                    );
                                }}
                            />

                            <Table.Column<StudentDebt>
                                width={'50px'}
                                align={'end'}
                                render={(record: StudentDebt) => (
                                    <DebtActions
                                        debt={record}
                                        studentId={studentId}
                                        branchId={props.currentBranchId}
                                    />
                                )}
                            />
                        </Table>
                    </ConfigProvider>
                </div>
            </MainSection>
        </StudentLayout>
    );
};

export const DebtActions = (props: {
    debt: StudentDebt;
    studentId: number;
    branchId: number;
}) => {
    const { debt, studentId, branchId } = props;

    if (debt.status !== DebtStatus.Active) return null;

    const [isCancelModalOpen, setCancelModalOpen] = useState(false);
    const [isPaidModalOpen, setPaidModalOpen] = useState(false);
    const [isUpdateModalOpen, setUpdateModalOpen] = useState(false);

    const [updateDebt] = useUpdateBranchStudentDebtMutation();
    const [cancelDebt] = useCancelBranchStudentDebtMutation();
    const [registerDebtPayment] = useRegisterBranchStudentDebtPaymentMutation();

    const onActionClick = (action: 'cancel' | 'paid' | 'update') => {
        if (action === 'cancel') {
            setCancelModalOpen(true);
        } else if (action === 'paid') {
            setPaidModalOpen(true);
        } else if (action === 'update') {
            setUpdateModalOpen(true);
        }
    };

    const onPaid = (values: { paidAt: Dayjs }) => {
        return registerDebtPayment({
            params: {
                branchId: branchId,
                studentId: studentId,
                debtId: debt.id,
            },
            body: {
                paidAt: values.paidAt.unix() * 1000,
            },
        })
            .then(() => {
                setPaidModalOpen(false);
                notifyAboutSuccess({
                    content: 'Оплата задолженности зарегистрирована',
                });
            })
            .catch((error: ResponseError) => {
                notifyAboutException({
                    sound: true,
                    content: error.message,
                });
            });
    };

    const onUpdate = async (values: DebtEditableFields): Promise<void> => {
        return updateDebt({
            params: {
                debtId: debt.id,
                studentId: studentId,
                branchId: branchId,
            },
            body: {
                amount:
                    values.amount !== Number(debt.amount)
                        ? values.amount
                        : null,
                description:
                    values.description !== debt.description
                        ? values.description
                        : null,
                originatedAt:
                    values.originatedAt.unix() !== debt.originatedAt
                        ? values.originatedAt.unix() * 1000
                        : null,
            },
        })
            .then(() => {
                setUpdateModalOpen(false);
                notifyAboutSuccess({
                    content: 'Информация о задолженности обновлена',
                });
            })
            .catch((error: ResponseError) => {
                notifyAboutException({
                    sound: true,
                    content: error.message,
                });
            });
    };

    const onCancel = async (): Promise<void> => {
        return cancelDebt({
            params: {
                branchId: branchId,
                studentId: studentId,
                debtId: debt.id,
            },
        })
            .then(() => {
                setCancelModalOpen(false);
                notifyAboutSuccess({
                    content: 'Задолженность аннулирована',
                });
            })
            .catch((error: ResponseError) => {
                notifyAboutException({
                    sound: true,
                    content: error.message,
                });
            });
    };

    const formik = useFormik<{ paidAt: Dayjs }>({
        initialValues: {
            paidAt: dayjs(),
        },
        validate: () => {
            const errors: FormikErrors<any> = {};
            return errors;
        },
        onSubmit: onPaid,
    });

    return (
        <>
            <ApplicationModal
                header={'Обновление задолженности'}
                isOpen={isUpdateModalOpen}
                onClose={() => setUpdateModalOpen(false)}
            >
                <DebtForm
                    debt={debt}
                    onSubmit={onUpdate}
                    onClose={() => setUpdateModalOpen(false)}
                />
            </ApplicationModal>

            <ApplicationModal
                header={'Погашение задолженности'}
                isOpen={isPaidModalOpen}
                onClose={() => setPaidModalOpen(false)}
            >
                <Form prohibitAutoComplete={true} formik={formik}>
                    <FormRow>
                        <DateField
                            formik={formik}
                            name={'paidAt'}
                            label={'Дата погашения'}
                            description={
                                'Дата, в которую контрагент внес платеж'
                            }
                            maxDate={dayjs()}
                            minDate={dayjs(debt.originatedAt)}
                        />
                    </FormRow>

                    <FormButtons
                        onCancel={() => setPaidModalOpen(false)}
                        submitLabel={'Погасить'}
                    />
                </Form>
            </ApplicationModal>

            <ApplicationModal
                header={'Аннулирование задолженности'}
                isOpen={isCancelModalOpen}
                onClose={() => setCancelModalOpen(false)}
            >
                <Form onSubmit={onCancel}>
                    <FormRow>
                        После аннулирования задолженность будет недоступна к
                        дальнейшему редактированию
                    </FormRow>

                    <FormButtons
                        onCancel={() => setCancelModalOpen(false)}
                        submitLabel={'Аннулировать'}
                    />
                </Form>
            </ApplicationModal>

            <Dropdown
                overlayClassName={'actions-dropdown'}
                arrow={false}
                placement={'bottomRight'}
                trigger={['click']}
                menu={{
                    onClick: ({ key }) =>
                        onActionClick(key as 'cancel' | 'paid' | 'update'),
                    items: [
                        {
                            key: '1',
                            label: 'Действия',
                            disabled: true,
                        },
                        {
                            key: 'update',
                            label: 'Изменить',
                        },
                        {
                            key: 'cancel',
                            label: 'Аннулировать',
                        },
                        {
                            key: 'paid',
                            label: 'Оплачена',
                        },
                    ],
                }}
            >
                <MoreOutlined />
            </Dropdown>
        </>
    );
};

export const RegisterDebtWidget = (props: {
    student: Student | undefined;
    branchId: number;
}) => {
    const [isModalOpen, setModalOpen] = useState(false);

    const [registerDebt] = useRegisterBranchStudentDebtMutation();

    const onDebtRegister = async (data: DebtEditableFields) => {
        if (!props.student) return;

        return registerDebt({
            params: {
                branchId: props.branchId,
                studentId: props.student.id,
            },
            body: {
                amount: data.amount,
                description: data.description,
                originatedAt: data.originatedAt.unix() * 1000,
            },
        })
            .unwrap()
            .then(() => {
                setModalOpen(false);
                notifyAboutSuccess({
                    content: 'Задолженность зарегистрирована',
                });
            })
            .catch((error: ResponseError) => {
                notifyAboutException({
                    sound: true,
                    content: error.message,
                });
            });
    };

    return (
        <>
            <ApplicationModal
                header={'Регистрация задолженности'}
                isOpen={isModalOpen}
                onClose={() => setModalOpen(false)}
            >
                <DebtForm
                    debt={null}
                    onSubmit={onDebtRegister}
                    onClose={() => setModalOpen(false)}
                />
            </ApplicationModal>
            <PrimaryButton
                disabled={props.student === undefined}
                label={'Новая задолженность'}
                onClick={() => setModalOpen(true)}
            />
        </>
    );
};

export type DebtEditableFields = {
    amount: number;
    description: string;
    originatedAt: Dayjs;
};

export const DebtForm = (props: {
    debt: StudentDebt | null;
    onSubmit?: (debt: DebtEditableFields) => Promise<void>;
    onClose?: () => void;
}) => {
    const formik = useFormik<DebtEditableFields>({
        initialValues: {
            amount: props.debt !== null ? Number(props.debt?.amount) : 0,
            description:
                props.debt !== null
                    ? props.debt.description
                    : 'Оплата за обучение',
            originatedAt:
                props.debt !== null ? dayjs(props.debt.originatedAt) : dayjs(),
        },
        validate: (values: DebtEditableFields) => {
            const { amount, description } = values;

            const errors: FormikErrors<DebtEditableFields> = {};

            if (amount <= 0) {
                errors.amount = 'Сумма должна больше нуля';
            }

            if (description.length < 5) {
                errors.description = 'Описание должно быть заполнено';
            }

            return errors;
        },
        onSubmit: async values => {
            if (props.onSubmit) {
                await props.onSubmit(values);
            }
        },
    });

    return (
        <Form formik={formik} prohibitAutoComplete={true}>
            <FormRow>
                <IntegerField
                    autoFocus={true}
                    formik={formik}
                    name={'amount'}
                    description={'Сумма в рублях, которую задолжал студент'}
                    label={'Сумма'}
                />
            </FormRow>

            <FormRow>
                <TextField
                    formik={formik}
                    name={'description'}
                    description={'Описание задолженности'}
                    label={'Описание'}
                />
            </FormRow>

            <FormRow>
                <DateField
                    formik={formik}
                    name={'originatedAt'}
                    description={'Дата регистрации'}
                    label={'Когда был пропущен платеж'}
                    maxDate={dayjs()}
                />
            </FormRow>

            <FormButtons formik={formik} />
        </Form>
    );
};
